/**
 * Single-select dropdown.
 *
 * Expands in place rather than opening a modal, which keeps the checkout on
 * one scroll surface and avoids a platform picker that would not match the
 * approved visual language.
 */

import { memo, useState } from 'react';
import { Pressable, ScrollView, StyleSheet, View } from 'react-native';

import { colors, radius, space } from '../../theme';
import { CheckIcon, ChevronIcon, Glyph } from '../common/Icon';
import { Text } from '../common/Text';

export interface SelectOption {
  value: string;
  label: string;
}

export interface SelectFieldProps {
  label: string;
  options: SelectOption[];
  value: string | null;
  onChange: (value: string) => void;
  placeholder?: string;
  required?: boolean;
  error?: string | null;
  icon?: string;
  /** Shows the chosen option and refuses to open. Used where the operator
   *  offers exactly one, so a list would only imply a choice that isn't. */
  disabled?: boolean;
  testID?: string;
}

export const SelectField = memo(function SelectField({
  label,
  options,
  value,
  onChange,
  placeholder = 'Select an option',
  required = false,
  error,
  icon = 'location',
  disabled = false,
  testID = 'select',
}: SelectFieldProps) {
  const [open, setOpen] = useState(false);
  const selected = options.find((option) => option.value === value);

  return (
    <View testID={testID}>
      <Text variant="label" tone="secondary">
        {label}
        {required ? <Text tone="danger"> *</Text> : null}
      </Text>

      <Pressable
        testID={`${testID}-trigger`}
        onPress={() => !disabled && setOpen((current) => !current)}
        accessibilityRole="button"
        accessibilityState={{ expanded: open, disabled }}
        disabled={disabled}
        accessibilityLabel={label}
        accessibilityValue={{ text: selected?.label ?? placeholder }}
        style={[
          styles.trigger,
          error ? styles.triggerError : null,
          disabled ? styles.triggerDisabled : null,
        ]}
      >
        <Glyph name={icon} size={16} />
        <Text variant="body" tone={selected ? 'primary' : 'disabled'} style={styles.value}>
          {selected?.label ?? placeholder}
        </Text>
        {disabled ? null : <ChevronIcon />}
      </Pressable>

      {open && !disabled ? (
        <View style={styles.panel} testID={`${testID}-options`}>
          <ScrollView style={styles.panelScroll} nestedScrollEnabled>
            {options.map((option, index) => {
              const isSelected = option.value === value;
              return (
                <Pressable
                  key={option.value}
                  testID={`${testID}-option-${option.value}`}
                  onPress={() => {
                    onChange(option.value);
                    setOpen(false);
                  }}
                  accessibilityRole="menuitem"
                  accessibilityState={{ selected: isSelected }}
                  style={[styles.option, index > 0 && styles.optionDivided]}
                >
                  <Text variant="body" style={styles.optionLabel}>
                    {option.label}
                  </Text>
                  {isSelected ? <CheckIcon size={18} /> : null}
                </Pressable>
              );
            })}
          </ScrollView>
        </View>
      ) : null}

      {error ? (
        <Text
          variant="caption"
          tone="danger"
          accessibilityLiveRegion="polite"
          style={styles.error}
          testID={`${testID}-error`}
        >
          {error}
        </Text>
      ) : null}
    </View>
  );
});

const styles = StyleSheet.create({
  trigger: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: space.sm,
    borderWidth: 1,
    borderColor: colors.border.default,
    borderRadius: radius.md,
    paddingHorizontal: space.md,
    minHeight: 52,
    marginTop: space.xs,
    backgroundColor: colors.bg.surface,
  },
  triggerError: {
    borderColor: colors.text.danger,
  },
  triggerDisabled: {
    backgroundColor: colors.bg.surfaceSunken,
  },
  value: {
    flex: 1,
  },
  panel: {
    marginTop: space.xs,
    borderWidth: 1,
    borderColor: colors.border.default,
    borderRadius: radius.md,
    backgroundColor: colors.bg.surface,
    overflow: 'hidden',
  },
  panelScroll: {
    maxHeight: 220,
  },
  option: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: space.md,
    paddingVertical: space.md,
    gap: space.sm,
  },
  optionDivided: {
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: colors.border.subtle,
  },
  optionLabel: {
    flex: 1,
  },
  error: {
    marginTop: space.xs,
  },
});
